import { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ensure, useNotifications } from "sprint-common-web";

import { UpdateCloudAssetContentRequest } from "../../../../../../../../common/api/master-api/assets/details/UpdateCloudAssetContentRequest";
import { ValidateCloudAssetContentRequest } from "../../../../../../../../common/api/master-api/assets/details/ValidateCloudAssetContentRequest";
import {
    isAncillaryContentDetails,
    isFullTvSeriesContentDetails,
    isMovieContentDetails
} from "../../../../../../../../common/types/ContentDetails";
import { ContentReference } from "../../../../../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../../../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../../../../../../common/types/ContentSubtype";
import { ContentAssociationFormState } from "../../../../../../../../content-ingestion/common/content/types/ContentAssociationFormState";
import { getCloudAssetDetailsPageHref } from "../../../../../../../AssetsRoutes";
import { CloudAssetDetails } from "../../../../../CloudAssetDetails";
import { useCloudAssetDetails } from "../../../../../common/hooks/useCloudAssetDetails";
import { resolveCloudAssetHref } from "../../../common/resolveCloudAssetHref";
import { useEditCloudAssetContent } from "./useEditCloudAssetContent";

export const useEditCloudAssetContentForm = () => {
    const [defaultContentState, setDefaultContentState] = useState<ContentAssociationFormState>(
        {} as ContentAssociationFormState
    );

    const [isDefaultContentLoading, setIsDefaultContentLoading] = useState<boolean>(true);

    const { success, error } = useNotifications();

    const { data: assetDetails } = useCloudAssetDetails();
    const { validateCloudAssetContent, updateCloudAssetContent } = useEditCloudAssetContent({
        invalidationKey: assetDetails?.assetId
    });

    const { assetId } = useParams<{ assetId: string }>();
    const history = useHistory();

    const onSubmit = async (contentReference?: ContentReference) => {
        ensure(contentReference);

        const request: UpdateCloudAssetContentRequest = {
            assetId,
            contentReference
        };

        try {
            const result = await updateCloudAssetContent.mutateAsync(request);

            goToNewAssetDetails(result.newAssetId, result.newAssetContentSubtype);

            success("New asset created successfully");
        } catch (e) {
            error("Something went wrong while creating new asset");
        }
    };

    const validate = (contentReference: ContentReference) => {
        const request: ValidateCloudAssetContentRequest = {
            assetId,
            contentReference
        };

        return validateCloudAssetContent.mutateAsync(request);
    };

    const backToAssetDetails = () => {
        const path = getCloudAssetDetailsPageHref(assetId);
        history.push(path);
    };

    const goToNewAssetDetails = (newAssetId: string, newAssetContentSubtype: ContentSubtype) => {
        const newHref = resolveCloudAssetHref(newAssetContentSubtype, newAssetId);
        history.push(newHref);
    };

    return {
        defaultContentState,
        setDefaultContentState,
        isDefaultContentLoading,
        assetId,
        setIsDefaultContentLoading,
        backToAssetDetails,
        goToNewAssetDetails,
        onSubmit,
        validate,
        assetDetails
    };
};

export const mapToContentAssociationFormState = (cloudAssetDetails: CloudAssetDetails): ContentAssociationFormState => {
    const { content } = cloudAssetDetails;

    if (isMovieContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Movie,
            contentId: content.movie.id,
            versionIds: content.versionSummary.ids
        };
    }

    if (isFullTvSeriesContentDetails(content)) {
        return {
            contentType: ContentReferenceType.TvSeries,
            contentId: content.tvSeries.id,
            episodeId: content.episode.id,
            seasonId: content.season.id,
            versionIds: content.versionSummary.ids
        };
    }

    if (isAncillaryContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Ancillary,
            contentId: content.ancillary.id,
            airlineId: content.airline.id
        };
    }

    throw new Error("Unsupported content subtype");
};
